import type {
	FormConfig,
	FormField,
	Option,
	SubmissionData,
} from "../../types/form";

// Produce a minimal InstaLaw Cloud collection schema JSON from a FormConfig.
// This can be POSTed to an InstaLaw Cloud endpoint to create a new collection definition.
export function formConfigToCollectionSchema(config: FormConfig) {
	const fields = config.sections.flatMap((s) => s.fields);

	return {
		name: `${config.org}__${config.slug}`,
		schema: fields.map(mapFieldToSchemaField),
		settings: {
			title: config.title,
			source: "end-user-ui",
			formSlug: config.slug,
			org: config.org,
		},
	};
}

function mapFieldToSchemaField(field: FormField) {
	const base = {
		id: field.id,
		name: field.id,
		required: !!field.required,
		label: field.label,
		type: "text" as const,
	};

	switch (field.type) {
		case "number":
			return { ...base, type: "number" };
		case "date":
			return { ...base, type: "date" };
		case "email":
			return { ...base, type: "email" };
		case "checkbox":
			return { ...base, type: "boolean" };
		case "select":
		case "radio":
			return {
				...base,
				type: "text",
				options: (field as FormField & { options: Option[] }).options?.map(
					(o: Option) => o.value,
				),
			};
		case "file":
			return {
				...base,
				type: "file",
				multiple:
					(field as FormField & { multiple?: boolean }).multiple || false,
			};
		default:
			return base;
	}
}

// Convert a submitted form payload into a collection record (key-value) JSON.
export function submissionToCollectionRecord(
    config: FormConfig,
    submission: SubmissionData,
) {
    const fields = config.sections.flatMap((s) => s.fields);
    const record: Record<string, unknown> = {};
    const known = new Set(fields.map((f) => f.id));

    for (const f of fields) {
        const val = (submission as SubmissionData)[f.id];
        if (f.type === "file") {
			// Support both FileList (pre-upload) and uploaded items with {fileId|url}
			if (val && typeof val === "object" && "length" in (val as FileList)) {
				const arr = Array.from(val as FileList);
				type UploadedFile = File & { fileId?: string; url?: string };
				record[f.id] = arr.map((item) => {
					const uf = item as UploadedFile;
					if (uf && typeof uf === "object" && (uf.fileId || uf.url)) {
						const { name, size, type, fileId, url } = uf;
						return { name, size, type, fileId, url };
					}
					return { name: item.name, size: item.size, type: item.type };
				});
			} else {
				record[f.id] = [];
			}
		} else if (f.type === "checkbox") {
			record[f.id] = Boolean(val);
        } else {
            record[f.id] = val ?? null;
        }
    }

    // Pass-through for unknown file-like fields present in submission
    for (const [key, val] of Object.entries(submission)) {
        if (known.has(key)) continue;
        // Treat arrays as file arrays
        if (Array.isArray(val)) {
            type UploadedFileLike = {
                name?: string;
                size?: number;
                type?: string;
                fileId?: string;
                url?: string;
            };
            record[key] = (val as unknown[]).map((item) => {
                if (item && typeof item === "object") {
                    const { name, size, type, fileId, url } = item as UploadedFileLike;
                    if (name || size || type || fileId || url) {
                        return { name, size, type, fileId, url };
                    }
                }
                return item as unknown;
            });
            continue;
        }
        // Treat FileList
        if (val && typeof val === "object" && "length" in (val as { length: number })) {
            const arr = Array.from(val as FileList);
            record[key] = arr.map((item) => {
                const f = item as File & { fileId?: string; url?: string };
                const { name, size, type, fileId, url } = f;
                return { name, size, type, fileId, url };
            });
            continue;
        }
        // If explicitly null for a file-like field, normalize to []
        if (val === null) {
            record[key] = [];
        }
    }

    return record;
}
